// Report document body (DESIGN_SPEC §11.4.3–11.4.4): the sticky figure index, then the journal rows the plan
// lays out (panels render their own .cell sections; a lone panel in a paired row spans the full width) and one
// line naming the sections that were absent.
//   <ReportBody ctx plan index? note? />   index = the sticky figure index (completed and partial documents)
//   <PanelBoundary label no inline?>…</PanelBoundary>   a panel that throws becomes an inline alert in its own cell
//   <FigRow>…</FigRow>                     one .journal row
import { Component, type ReactNode } from "react";
import { Alert, Empty, FigHead } from "@/components/ui";
import type { ReportCtx } from "@/lib/derive/types";
import { jobName } from "@/lib/records";

import { FigIndex } from "./FigIndex";
import { absentLine, type DocumentPlan, type PlannedFig } from "./figOrder";

type BoundaryProps = { label: string; no: number; inline?: boolean; children: ReactNode };

export class PanelBoundary extends Component<BoundaryProps, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (!this.state.error) return this.props.children;
    const alert = (
      <Alert head="Couldn't draw this figure" className="mt-5">
        <span translate="no">{this.state.error.message || this.state.error.name}</span>
      </Alert>
    );
    if (this.props.inline) return alert;
    return (
      <section className="cell fig" aria-labelledby={`fig-${this.props.no}-t`}>
        <FigHead no={this.props.no} label={this.props.label} />
        {alert}
      </section>
    );
  }
}

/** A journal row; a lone cell (its pair had no data) spans the row and drops the side rule. */
export function FigRow({ children }: { children: ReactNode }) {
  return <div className="journal [&>.cell:only-child]:col-span-12 [&>.side:only-child]:border-l-0 [&>.side:only-child]:pl-0">{children}</div>;
}

function PlannedPanel({ fig, ctx }: { fig: PlannedFig; ctx: ReportCtx }) {
  const C = fig.Component;
  return (
    <PanelBoundary label={fig.label} no={fig.no}>
      <C ctx={ctx} no={fig.no} />
    </PanelBoundary>
  );
}

export function ReportBody({ ctx, plan, index = true, note }: { ctx: ReportCtx; plan: DocumentPlan; index?: boolean; note?: ReactNode }) {
  const absent = note ?? absentLine(plan.absent, ctx);
  return (
    <>
      {index && <FigIndex figs={plan.figs} verdict={ctx.stream.verdict} state={ctx.stream.state} name={jobName(ctx.job)} />}
      <div className="page">
        {plan.rows.map((row) => (
          <FigRow key={row.map((f) => f.section).join("+")}>
            {row.map((fig) => (
              <PlannedPanel key={fig.section} fig={fig} ctx={ctx} />
            ))}
          </FigRow>
        ))}
        {absent && (
          <div className={plan.rows.length ? "border-t border-rule-cell" : undefined}>
            <Empty inline rule={false}>
              {absent}
            </Empty>
          </div>
        )}
      </div>
    </>
  );
}
